import React from 'react';
import { Reminder, REMINDER_PRESETS, createReminder, formatReminderPreview } from '../../types/reminderTypes';
import { EVENT_TYPE_CONFIGS, EventType } from '../../types/eventTypes';

interface ReminderSelectorProps {
    reminders: Reminder[];
    onChange: (reminders: Reminder[]) => void;
    eventType?: EventType;
    eventDate?: Date;
}

const ReminderSelector: React.FC<ReminderSelectorProps> = ({
    reminders,
    onChange,
    eventType,
    eventDate,
}) => {
    // Get suggested reminders based on event type
    const suggestedDays = eventType
        ? EVENT_TYPE_CONFIGS[eventType]?.defaultReminders || [7, 3, 1]
        : [7, 3, 1];

    const toggleReminder = (daysBefore: number) => {
        const existing = reminders.find(r => r.daysBefore === daysBefore);

        if (existing) {
            // Remove if exists
            onChange(reminders.filter(r => r.daysBefore !== daysBefore));
        } else {
            // Add new reminder
            onChange([...reminders, createReminder(daysBefore)]);
        }
    };

    const isSelected = (daysBefore: number) => {
        return reminders.some(r => r.daysBefore === daysBefore && r.enabled);
    };

    const activeCount = reminders.filter(r => r.enabled).length;

    return (
        <div className="space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 font-semibold">
                    🔔 Nhắc nhở
                </span>
                {activeCount > 0 && (
                    <span className="text-xs text-blue-600 font-medium">
                        {activeCount} nhắc nhở đã bật
                    </span>
                )}
            </div>

            {/* Preset Chips */}
            <div className="flex flex-wrap gap-2">
                {REMINDER_PRESETS.map(preset => {
                    const selected = isSelected(preset.daysBefore);
                    const isSuggested = suggestedDays.includes(preset.daysBefore);

                    return (
                        <button
                            key={preset.daysBefore}
                            type="button"
                            onClick={() => toggleReminder(preset.daysBefore)}
                            className={`
                                px-3 py-2 rounded-full text-sm font-medium
                                transition-all duration-200 border
                                ${selected
                                    ? 'bg-blue-500 border-blue-500 text-white shadow-sm'
                                    : isSuggested
                                        ? 'bg-blue-100 border-blue-400 text-blue-700 hover:bg-blue-200'
                                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }
                            `}
                        >
                            {selected && <span className="mr-1">✓</span>}
                            {preset.label}
                            {isSuggested && !selected && (
                                <span className="ml-1 text-xs opacity-70">★</span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Reminder Preview */}
            {eventDate && reminders.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-3 space-y-1 border border-blue-200">
                    <span className="text-xs text-gray-600 font-medium">Sẽ nhắc vào:</span>
                    <div className="flex flex-wrap gap-2">
                        {reminders
                            .filter(r => r.enabled)
                            .sort((a, b) => b.daysBefore - a.daysBefore)
                            .map(r => (
                                <span
                                    key={r.id}
                                    className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-medium"
                                >
                                    📅 {formatReminderPreview(eventDate, r.daysBefore)}
                                </span>
                            ))
                        }
                    </div>
                </div>
            )}

            {/* Suggested hint */}
            {eventType && activeCount === 0 && (
                <p className="text-xs text-gray-600">
                    💡 Gợi ý cho {EVENT_TYPE_CONFIGS[eventType]?.label}: {suggestedDays.join('/')} ngày trước
                </p>
            )}
        </div>
    );
};

export default ReminderSelector;
